<template>
    <vx-modal v-model:visible="visible" padding="standard" :preventClosing="submitting" size="md">
        <template v-slot:header>Coupon Code Import</template>
        <div class="text-sm text-slate-800">
            <div class="mb-4 grid grid-cols-1 gap-4 md:grid-cols-12">
                <div class="col-span-7">
                    <vx-label>Template</vx-label>
                    <div>
                        Download a template to get going. Checkout out more advanced examples in our
                        <a href="https://explore.voxie.com/voxiesupport" target="_blank" rel="noopener noreferrer"
                            >documentation</a
                        >.
                    </div>
                </div>
                <div class="col-span-5">
                    <csv-template :downloadHref="csvUrl">
                        <template v-slot:head>
                            <tr>
                                <th>coupon_code</th>
                            </tr>
                        </template>

                        <tr>
                            <td>vg101121</td>
                        </tr>
                    </csv-template>
                </div>
            </div>
            <div>
                <div>
                    <vx-label class="mb-3">
                        {{ file ? 'Upload Preview' : 'File Upload' }}
                        <template v-if="file" v-slot:description>
                            <div class="text-slate-800">
                                Last chance to confirm everything looks good before we run the import.
                            </div>
                        </template>
                    </vx-label>
                </div>
                <drag-and-drop-file
                    v-model="file"
                    :icon="faFileCsv"
                    :validate="validatePromotionCouponsCsv"
                    accept=".csv"
                ></drag-and-drop-file>
                <vx-alert v-if="!file" class="mt-4" color="info" :closeable="false">
                    Files must be <strong>CSV</strong> and less than <strong>100MB</strong>.
                </vx-alert>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="!submitting && (visible = false)"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    type="button"
                    data-test="submit-button"
                    @click="submit()"
                    :disabled="!file"
                    :loading="submitting"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Import <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { faCirclePlus, faFileCsv } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxModal, VxButton, VxLabel, VxAlert } from '@voxie/frontend-components';
import { ref } from 'vue';
import CsvTemplate from '~/components/general/CsvTemplate.vue';
import DragAndDropFile from '~/components/general/DragAndDropFile.vue';
import { validatePromotionCouponsCsv } from '~/components/promotions/validatePromotionCouponsCsv';
import csvUrl from '@csv/coupons_csv_upload_template.csv';
import { promotionCouponsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';

const teamId = Spark.state.currentTeam.id;

const toasted = useToasted();

const emit = defineEmits(['uploaded']);
const props = defineProps({
    promotionId: {
        type: Number,
        required: true,
    },
});

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const submitting = ref(false);

const file = ref();

const submit = async () => {
    if (submitting.value || !file.value) {
        return;
    }
    submitting.value = true;

    try {
        const { data } = await promotionCouponsClient.generateImportPresignedUrl(
            teamId,
            props.promotionId,
            file.value.name
        );

        if (!data?.upload_url) {
            throw new Error('Could not parse the upload url');
        }

        await promotionCouponsClient.importCsvFileToS3(data.upload_url, file.value);

        emit('uploaded', {
            import_id: data.import_id,
            file_name: file.value.name,
        });
        visible.value = false;
    } catch (error) {
        console.error(error);
        toasted.global.platform_error();
    } finally {
        submitting.value = false;
    }
};
</script>
